/**
 * Image Aspect Ratio Utilities
 *
 * Utilities for extracting aspect ratios from image metadata and preserving
 * aspect ratio when positioning shapes with image fills.
 *
 * Note: Pixel dimensions may include transparent padding. Visual dimensions
 * (actual content bounding box) are preferred when available.
 */
/**
 * Calculate the visual bounding box of an image (excluding transparent padding).
 * Returns the actual content dimensions.
 *
 * @param base64Data Base64-encoded image data (with or without data URI prefix)
 * @returns Promise resolving to visual dimensions { width, height } or undefined if calculation fails
 */
export async function calculateVisualDimensionsFromBase64(base64Data) {
    return new Promise((resolve) => {
        try {
            // Remove data URI prefix if present
            const base64 = base64Data.includes(',')
                ? base64Data.split(',')[1]
                : base64Data;
            // Create image element to load
            const img = new Image();
            img.onload = () => {
                try {
                    // Create canvas to analyze pixel data
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        console.warn('[ImageAspectRatio] Could not get canvas context');
                        resolve(undefined);
                        return;
                    }
                    // Draw image to canvas
                    ctx.drawImage(img, 0, 0);
                    // Get image data
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const data = imageData.data;
                    // Find bounding box of non-transparent pixels
                    let minX = canvas.width;
                    let maxX = 0;
                    let minY = canvas.height;
                    let maxY = 0;
                    // Scan pixels to find content bounds
                    for (let y = 0; y < canvas.height; y++) {
                        for (let x = 0; x < canvas.width; x++) {
                            const index = (y * canvas.width + x) * 4;
                            const alpha = data[index + 3]; // Alpha channel
                            // If pixel is not fully transparent
                            if (alpha > 0) {
                                if (x < minX)
                                    minX = x;
                                if (x > maxX)
                                    maxX = x;
                                if (y < minY)
                                    minY = y;
                                if (y > maxY)
                                    maxY = y;
                            }
                        }
                    }
                    // Calculate visual dimensions
                    const visualWidth = maxX - minX + 1;
                    const visualHeight = maxY - minY + 1;
                    // If no content found, use full dimensions
                    if (visualWidth <= 0 || visualHeight <= 0) {
                        console.warn('[ImageAspectRatio] No visible content found, using full dimensions');
                        resolve({ width: img.width, height: img.height });
                        return;
                    }
                    console.log(`[ImageAspectRatio] Visual dimensions: ${visualWidth}x${visualHeight} (pixel: ${img.width}x${img.height})`);
                    resolve({ width: visualWidth, height: visualHeight });
                }
                catch (error) {
                    console.warn('[ImageAspectRatio] Error calculating visual dimensions:', error);
                    // Fallback to pixel dimensions
                    resolve({ width: img.width, height: img.height });
                }
            };
            img.onerror = () => {
                console.warn('[ImageAspectRatio] Failed to load image for visual dimension calculation');
                resolve(undefined);
            };
            // Set src to data URI
            img.src = base64Data.startsWith('data:')
                ? base64Data
                : `data:image/png;base64,${base64}`;
        }
        catch (error) {
            console.warn('[ImageAspectRatio] Error in calculateVisualDimensionsFromBase64:', error);
            resolve(undefined);
        }
    });
}
/**
 * Extract aspect ratio from image metadata.
 * Handles various storage patterns used by different sources:
 * - Visual dimensions: metadata.visualWidth / metadata.visualHeight (preferred - actual content size)
 * - Top-level: metadata.width / metadata.height (pixel dimensions, may include padding)
 * - Nested: metadata.dimensions.width / metadata.dimensions.height (Brandfetch, Wikimedia)
 *
 * Note: Pixel dimensions may include transparent padding, so visual dimensions are preferred.
 *
 * @param metadata Image metadata record
 * @param defaultRatio Fallback ratio if dimensions not found (default: 1.5)
 * @returns Aspect ratio (width / height)
 */
export function getAspectRatioFromMetadata(metadata, defaultRatio = 1.5) {
    if (!metadata)
        return defaultRatio;
    // Prefer visual dimensions if available (actual content size, excluding padding)
    const visualWidth = metadata.visualWidth;
    const visualHeight = metadata.visualHeight;
    if (visualWidth && visualHeight && visualWidth > 0 && visualHeight > 0) {
        return visualWidth / visualHeight;
    }
    // Fall back to pixel dimensions (may include transparent padding)
    const topWidth = metadata.width;
    const topHeight = metadata.height;
    if (topWidth && topHeight && topWidth > 0 && topHeight > 0) {
        return topWidth / topHeight;
    }
    // Check nested dimensions (legacy/fallback for direct access before normalization)
    const dimensions = metadata.dimensions;
    if (dimensions?.width && dimensions?.height && dimensions.width > 0 && dimensions.height > 0) {
        return dimensions.width / dimensions.height;
    }
    // Default fallback
    return defaultRatio;
}
/**
 * Get aspect ratio from image data, calculating visual dimensions if needed.
 *
 * This function:
 * 1. First tries to get aspect ratio from metadata (preferring visual dimensions)
 * 2. If metadata doesn't have visual dimensions, calculates them from the image
 * 3. Falls back to pixel dimensions if calculation fails
 *
 * @param base64Data Base64-encoded image data
 * @param metadata Optional image metadata
 * @param defaultRatio Fallback ratio if all methods fail (default: 1.5)
 * @returns Promise resolving to aspect ratio (width / height)
 */
export async function getAspectRatioFromImage(base64Data, metadata, defaultRatio = 1.5) {
    // First, try metadata (prefers visual dimensions if available)
    const metadataRatio = getAspectRatioFromMetadata(metadata, defaultRatio);
    // If metadata has visual dimensions, use it
    if (metadata?.visualWidth && metadata?.visualHeight) {
        return metadataRatio;
    }
    // If metadata only has pixel dimensions, check if we should calculate visual dimensions
    // For now, we'll calculate visual dimensions as a fallback when pixel dimensions seem wrong
    // (e.g., square pixel dimensions but clearly non-square visual content)
    if (metadata?.width && metadata?.height) {
        const pixelRatio = metadata.width / metadata.height;
        void pixelRatio;
    }
    // Try to calculate visual dimensions from image data
    const visualDims = await calculateVisualDimensionsFromBase64(base64Data);
    if (visualDims && visualDims.width > 0 && visualDims.height > 0) {
        const visualRatio = visualDims.width / visualDims.height;
        console.log(`[ImageAspectRatio] Calculated visual aspect ratio: ${visualRatio.toFixed(2)} (${visualDims.width}x${visualDims.height})`);
        return visualRatio;
    }
    // Fall back to metadata ratio (which may be pixel-based)
    return metadataRatio;
}
/**
 * Calculate final position for image insertion, preserving aspect ratio.
 *
 * If both width and height are provided, uses the smaller constraint
 * to ensure the image fits within the bounding box.
 *
 * @param position The requested position (may have only width or height)
 * @param aspectRatio The image's aspect ratio (width/height)
 * @returns Resolved position with both width and height
 */
export function resolveImagePosition(position, aspectRatio) {
    const { left, top, width, height } = position;
    // Default aspect ratio if not available (assume square-ish)
    const ratio = aspectRatio || 1.5;
    // If both dimensions provided, use the smaller constraint
    if (width !== undefined && height !== undefined) {
        // Calculate what the other dimension would be for each constraint
        const heightIfWidthConstrained = width / ratio;
        const widthIfHeightConstrained = height * ratio;
        if (heightIfWidthConstrained <= height) {
            // Width is the tighter constraint - fits within height
            return { left, top, width, height: heightIfWidthConstrained };
        }
        else {
            // Height is the tighter constraint - fits within width
            return { left, top, width: widthIfHeightConstrained, height };
        }
    }
    // Only width provided
    if (width !== undefined) {
        return { left, top, width, height: width / ratio };
    }
    // Only height provided
    if (height !== undefined) {
        return { left, top, width: height * ratio, height };
    }
    // Neither provided (shouldn't happen with schema validation) - use defaults
    console.warn('[ImageAspectRatio] No width or height provided, using defaults');
    return { left, top, width: 400, height: 400 / ratio };
}
